// src/utils/spotify.js
// ----- Minimal Spotify browser client for your app (no import.meta) -----

// Preferred: set REACT_APP_AUTH_BASE (e.g. https://your-worker.workers.dev)
// Optional: set window.__AUTH_BASE at runtime (e.g. in index.html)
// Fallback: hardcode your Worker origin here.
const AUTH_BASE =
  (typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_AUTH_BASE) ||
  (typeof window !== "undefined" && window.__AUTH_BASE) ||
  "https://spotify-worker.benton-161.workers.dev"; // <- change if needed

function api(path) {
  return `${AUTH_BASE}${path}`;
}

function loadSpotifySDK() {
  return new Promise((resolve, reject) => {
    if (window.Spotify) return resolve();
    const s = document.createElement("script");
    s.src = "https://sdk.scdn.co/spotify-player.js";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

async function safeJson(res) {
  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) {
    const text = await res.text();
    throw new Error(
      `Expected JSON, got ${ct || "unknown"} — ${text.slice(0, 120)}`
    );
  }
  return res.json();
}

export async function fetchAccessToken() {
  const res = await fetch(api("/auth/token"), {
    credentials: "include",
    mode: "cors",
  });
  if (!res.ok) {
    let msg = `token_error_${res.status}`;
    try {
      const j = await res.json();
      if (j && j.error) msg = j.error;
    } catch {}
    throw new Error(msg);
  }
  return safeJson(res);
}

export async function ensureSpotifyAuth() {
  const res = await fetch(api("/auth/debug"), {
    credentials: "include",
    mode: "cors",
  });
  const dbg = await safeJson(res);
  if (!dbg.subject || !dbg.hasTokens) {
    const login = new URL(api("/auth/login"));
    login.searchParams.set("return_to", window.location.href);
    window.location.href = login.toString();
    return false; // redirected
  }
  return true;
}

let _player = null;
let _deviceId = null;

export async function getPlayer() {
  if (_player) return _player;

  await loadSpotifySDK();
  await new Promise((resolve) => {
    if (window.Spotify) return resolve();
    window.onSpotifyWebPlaybackSDKReady = () => resolve();
  });

  const { access_token: initialToken } = await fetchAccessToken();

  _player = new window.Spotify.Player({
    name: "Dylan Family Songbook",
    volume: 0.8,
    getOAuthToken: async (cb) => {
      try {
        const { access_token } = await fetchAccessToken();
        cb(access_token);
      } catch {
        cb(initialToken);
      }
    },
  });

  _player.addListener("ready", ({ device_id }) => {
    _deviceId = device_id;
  });
  _player.addListener("not_ready", () => {});
  _player.addListener("initialization_error", ({ message }) =>
    console.error(message)
  );
  _player.addListener("authentication_error", ({ message }) =>
    console.error(message)
  );
  _player.addListener("account_error", ({ message }) => console.error(message));

  // IMPORTANT: must be called from a user gesture chain
  if (typeof _player.activateElement === "function") {
    try {
      await _player.activateElement();
    } catch {}
  }

  await _player.connect();
  try {
    await _player.setVolume(0.8);
  } catch {}

  // wait for device id
  const started = Date.now();
  while (!_deviceId) {
    await new Promise((r) => setTimeout(r, 100));
    if (Date.now() - started > 8000) throw new Error("player_timeout");
  }
  return _player;
}

// ---- Device & playback helpers ----

async function transferPlayback(accessToken, { play = false } = {}) {
  await fetch("https://api.spotify.com/v1/me/player", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ device_ids: [_deviceId], play }),
  });
}

/** Ensure SDK device is active before playing (handles stubborn clients). */
async function ensureActiveWebPlayer(accessToken) {
  // make the SDK device the active device (without autoplay first)
  await transferPlayback(accessToken, { play: false });

  // poll for up to ~3s to see if it became active
  const t0 = Date.now();
  while (Date.now() - t0 < 3000) {
    const meRes = await fetch("https://api.spotify.com/v1/me/player", {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (meRes.status === 204) break;
    if (meRes.ok) {
      const state = await meRes.json();
      if (state?.device?.id === _deviceId && state?.device?.is_active) return;
    }
    await new Promise((r) => setTimeout(r, 200));
  }

  // last resort: force with play=true
  await transferPlayback(accessToken, { play: true });
}

async function searchTrack(accessToken, { title, artist = "Bob Dylan" }) {
  const q = `track:${title} artist:${artist}`;
  const url = `https://api.spotify.com/v1/search?type=track&limit=1&q=${encodeURIComponent(
    q
  )}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error("search_failed");
  const json = await res.json();
  return json?.tracks?.items?.[0] || null;
}

async function searchAlbum(accessToken, { album, artist = "Bob Dylan" }) {
  const q = `album:${album} artist:${artist}`;
  const url = `https://api.spotify.com/v1/search?type=album&limit=1&q=${encodeURIComponent(
    q
  )}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error("search_failed");
  const json = await res.json();
  return json?.albums?.items?.[0] || null;
}

async function playUri(accessToken, uri) {
  const res = await fetch(
    `https://api.spotify.com/v1/me/player/play?device_id=${_deviceId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ uris: [uri] }),
    }
  );
  if (res.status === 403) throw new Error("play_forbidden");
  if (!res.ok && res.status !== 204) {
    const t = await res.text().catch(() => "");
    throw new Error(`play_failed_${res.status} ${t}`);
  }
}

// ---- Public API ----

export async function playSongByTitle({ title, artist = "Bob Dylan" }) {
  const ok = await ensureSpotifyAuth();
  if (!ok) return; // redirected to login
  await getPlayer();
  const { access_token } = await fetchAccessToken();

  await ensureActiveWebPlayer(access_token);

  const match = await searchTrack(access_token, { title, artist });
  if (!match) throw new Error("track_not_found");
  await playUri(access_token, match.uri);
}

/** Best-effort album playback: album context -> album title track -> artist top track */
export async function playAlbumBestEffort({ album, artist = "Bob Dylan" }) {
  const ok = await ensureSpotifyAuth();
  if (!ok) return;
  await getPlayer();
  const { access_token } = await fetchAccessToken();

  await ensureActiveWebPlayer((accessToken = access_token));

  // 1) Try album context
  const hit = await searchAlbum(access_token, { album, artist });
  if (hit?.uri) {
    const res = await fetch(
      `https://api.spotify.com/v1/me/player/play?device_id=${_deviceId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${access_token}`,
        },
        body: JSON.stringify({ context_uri: hit.uri }),
      }
    );
    if (res.status === 204) return;
  }

  // 2) Try a track that matches the album title
  const t1 = await searchTrack(access_token, { title: album, artist });
  if (t1?.uri) {
    await playUri(access_token, t1.uri);
    return;
  }

  // 3) Last fallback: artist top/first match
  const t2 = await searchTrack(access_token, { title: artist, artist });
  if (t2?.uri) {
    await playUri(access_token, t2.uri);
    return;
  }

  throw new Error("album_not_found");
}
